import { useCallback, useMemo, useRef } from 'react'

import { getFieldIcon } from 'features/admin/fields/icons/utils'

import { FileUploadModal } from 'ui/components/FileUpload'
import { useGlobalModal } from 'ui/components/Modal/useGlobalModal'

export type LayoutEditorMultiImageFieldRef = {
    type: 'field'
    fieldApiName: string
}

export type LayoutEditorMultiImageFileRef = {
    type: 'file'
    url: string
    filename: string
}

export type LayoutEditorMultiImageRef =
    | LayoutEditorMultiImageFieldRef
    | LayoutEditorMultiImageFileRef

type UseLayoutEditorMultiImageControlStateProps = {
    value: LayoutEditorMultiImageRef[]
    onChange: (value: LayoutEditorMultiImageRef[]) => void
    fields: FieldDto[]
    maxSelectedFields?: number
    maxSelectedImages?: number
    restrictSingleType?: boolean
}

export function useLayoutEditorMultiImageControlState({
    value,
    onChange,
    fields,
    maxSelectedFields,
    maxSelectedImages,
    restrictSingleType,
}: UseLayoutEditorMultiImageControlStateProps) {
    const valueRef = useRef(value)
    valueRef.current = value

    const addToValue = useCallback(
        (newValue: LayoutEditorMultiImageRef) => {
            onChange([...valueRef.current, newValue])
        },
        [onChange]
    )

    const removeFromValue = useCallback(
        (selectFn: (value: LayoutEditorMultiImageRef) => boolean) => {
            const newValue = valueRef.current.filter((v) => !selectFn(v))
            onChange(newValue)
        },
        [onChange]
    )

    const selectedImages = useMemo(() => value.filter((v) => v.type === 'file'), [value])
    const onRemoveImage = (url: string) => {
        removeFromValue((v) => v.type === 'file' && v.url === url)
    }

    const { show } = useGlobalModal(FileUploadModal)
    const onUploadClick = useCallback(() => {
        // Close the dropdown before opening the file picker.
        setTimeout(() => {
            show({
                maxFiles: maxSelectedImages ?? 1,
                zIndex: 9999,
                accept: 'image/*',
                onUploadSuccess: (files) => {
                    const newFiles: LayoutEditorMultiImageFileRef[] = files.map((file) => ({
                        type: 'file',
                        url: file.url ?? '',
                        filename: file.name ?? '',
                    }))

                    for (const file of newFiles) {
                        addToValue(file)
                    }
                },
            })
        }, 200)
    }, [show, maxSelectedImages, addToValue])

    const fieldOptions = useMemo(
        () =>
            fields.map((field) => ({
                label: field.label,
                value: field.api_name,
                icon: getFieldIcon(field),
            })),
        [fields]
    )
    const selectedFieldsSet = useMemo(() => {
        const fieldSet = new Set<string>()
        for (const v of value) {
            if (v.type === 'field') {
                fieldSet.add(v.fieldApiName)
            }
        }

        return fieldSet
    }, [value])
    const isFieldSelected = useCallback(
        (fieldApiName: string) => selectedFieldsSet.has(fieldApiName),
        [selectedFieldsSet]
    )
    const selectedFieldOptions = useMemo(() => {
        return fieldOptions.filter((option) => isFieldSelected(option.value))
    }, [fieldOptions, isFieldSelected])
    const onAddField = useCallback(
        (fieldApiName: string) => {
            addToValue({ type: 'field', fieldApiName })
        },
        [addToValue]
    )
    const onRemoveField = useCallback(
        (fieldApiName: string) => {
            removeFromValue((v) => v.type === 'field' && v.fieldApiName === fieldApiName)
        },
        [removeFromValue]
    )

    const allowSelectFields = !restrictSingleType || selectedImages.length < 1
    const allowSelectImages = !restrictSingleType || selectedFieldsSet.size < 1

    const allowImageUpload =
        maxSelectedImages !== undefined &&
        selectedImages.length < maxSelectedImages &&
        allowSelectImages
    const allowFieldUpdate =
        maxSelectedFields !== undefined &&
        selectedFieldsSet.size < maxSelectedFields &&
        allowSelectFields

    const showValueDropdown =
        (value.length < 1 || !restrictSingleType) && (allowImageUpload || allowFieldUpdate)
    const showIndividualImageUpload = allowImageUpload && !showValueDropdown
    const showIndividualFieldUpdate = allowFieldUpdate && !showValueDropdown

    return {
        fieldOptions,
        isFieldSelected,
        onAddField,
        onRemoveField,
        selectedFieldOptions,
        selectedImages,
        onRemoveImage,
        onUploadClick,
        showValueDropdown,
        showIndividualImageUpload,
        showIndividualFieldUpdate,
        allowFieldUpdate,
        allowImageUpload,
    }
}
